import { FC } from 'react'
import clsx from 'clsx'

interface PaginationProps {
  className?: string
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
}

const Pagination: FC<PaginationProps> = ({ className, currentPage, totalPages, onPageChange }) => {
  const getPageItems = () => {
    const pages: (number | string)[] = []

    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      // 始终添加第一页
      pages.push(1)

      // 计算中间页码
      let start = Math.max(2, currentPage - 1)
      let end = Math.min(totalPages - 1, currentPage + 1)

      // 当前页靠近首页时
      if (currentPage <= 3) {
        end = 4
      }
      // 当前页靠近尾页时
      else if (currentPage >= totalPages - 2) {
        start = totalPages - 3
      }

      // 添加左侧省略号
      if (start > 2) {
        pages.push('...')
      }

      // 添加中间页码
      for (let i = start; i <= end; i++) {
        if (i > 1 && i < totalPages) {
          pages.push(i)
        }
      }

      // 添加右侧省略号
      if (end < totalPages - 1) {
        pages.push('...')
      }

      // 始终添加最后一页
      pages.push(totalPages)
    }

    return pages
  }

  if (totalPages === 0) return null

  return (
    <div
      className={clsx(
        'select-none',
        'flex items-center h-6 laptop:h-8 *:h-full',
        'justify-between laptop:justify-center',
        className
      )}
    >
      {/* 首页按钮 */}
      <div
        onClick={() => {
          if (currentPage === 1) return

          onPageChange(1)
        }}
        // disabled={currentPage === 1}
        className={clsx(
          'flex-none cursor-pointer',
          'bg-[url("/icons/icon_l_first_arrow.svg")] bg-cover bg-no-repeat bg-[1px_center]',
          'w-6 opacity-50 rounded-sm',
          'laptop:w-8',
          'hover:opacity-100 hover:bg-gray-300',
          {
            '!opacity-20 !cursor-not-allowed hover:!bg-[unset]': currentPage === 1,
          }
        )}
      />

      {/* 上一页按钮 */}
      <div
        onClick={() => {
          if (currentPage === 1) return

          onPageChange(currentPage - 1)
        }}
        // disabled={currentPage === 1}
        className={clsx(
          'flex-none cursor-pointer',
          'bg-[url("/icons/icon_l_hover.svg")] bg-cover bg-no-repeat bg-[-1px_center]',
          'w-6 opacity-50 rounded-sm',
          'laptop:w-8',
          'hover:opacity-100 hover:bg-gray-300',
          {
            '!opacity-20 !cursor-not-allowed hover:!bg-[unset]': currentPage === 1,
          }
        )}
      />

      <div
        className={clsx(
          'flex items-center justify-center',
          'h-full *:w-8 laptop:*:w-10 *:h-full',
          'gap-1 laptop:gap-2'
        )}
      >
        {/* 页码按钮 */}
        {getPageItems().map((item, index) =>
          typeof item === 'number' ? (
            <div
              key={item}
              onClick={() => onPageChange(item)}
              className={clsx(
                'cursor-pointer rounded-sm flex justify-center items-center',
                'text-black/50 text-sm laptop:text-base font-semibold laptop:font-base',
                'hover:text-black/90 hover:bg-gray-300',
                {
                  '!text-white bg-blue-600 hover:!text-white hover:!bg-blue-600': item === currentPage,
                }
              )}
            >
              {item}
            </div>
          ) : (
            // 省略号
            <div
              key={`ellipsis-${index}`}
              className="flex items-center justify-center gap-1 opacity-50 !w-4 laptop:!w-6"
            >
              {new Array(3).fill(null).map((_, i) => (
                <span key={i} className={clsx('w-0 h-0 border laptop:border-2 border-black rounded-full')}></span>
              ))}
            </div>
            //   <div className="w-10 text-center justify-start text-black/90 text-xl font-semibold leading-7">
            //     ···
            //   </div>
          )
        )}
      </div>

      {/* 下一页按钮 */}
      <div
        onClick={() => {
          if (currentPage === totalPages) return

          onPageChange(currentPage + 1)
        }}
        // disabled={currentPage === totalPages}
        className={clsx(
          'rotate-180 flex-none cursor-pointer',
          'bg-[url("/icons/icon_l_hover.svg")] bg-cover bg-no-repeat bg-[-1px_center]',
          'w-6 opacity-50 rounded-sm',
          'laptop:w-8',
          'hover:opacity-100 hover:bg-gray-300',
          {
            '!opacity-20 !cursor-not-allowed hover:!bg-[unset]': currentPage === totalPages,
          }
        )}
      />

      {/* 尾页按钮 */}
      <div
        onClick={() => {
          if (currentPage === totalPages) return

          onPageChange(totalPages)
        }}
        // disabled={currentPage === totalPages}
        className={clsx(
          'rotate-180 flex-none cursor-pointer',
          'bg-[url("/icons/icon_l_first_arrow.svg")] bg-cover bg-no-repeat bg-[1px_center]',
          'w-6 opacity-50 rounded-sm',
          'laptop:w-8',
          'hover:opacity-100 hover:bg-gray-300',
          {
            '!opacity-20 !cursor-not-allowed hover:!bg-[unset]': currentPage === totalPages,
          }
        )}
      />
    </div>
  )
}

export default Pagination
